import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const call = (path, body) => fetch(`${API}/api/admin/scoring-switch/${path}`, {
  method: body ? "POST" : "GET", credentials: "include",
  headers: body ? { "Content-Type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined,
}).then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.detail || "Request failed"); return d; });
const fmt = (v) => (v === null || v === undefined ? "—" : String(v));
const target = (row) => (row.scoring_version === "1.0" ? "1.1" : "1.0");

const Row = ({ row, onPreview }) => (
  <tr className="border-t border-slate-200" data-testid={`switch-row-${row.id}`}>
    <td className="py-2 pr-3 font-medium">{row.school_name || row.tenant_id}</td>
    <td className="py-2 pr-3">{row.period_label || "—"}{row.perspective === "portfolio" ? " (portfolio view)" : ""}</td>
    <td className="py-2 pr-3">{row.status}</td>
    <td className="py-2 pr-3" data-testid={`switch-version-${row.id}`}>v{row.scoring_version}{row.scoring_switch_id ? " · switched" : ""}</td>
    <td className="py-2 text-right">
      <button type="button" onClick={() => onPreview(row)} className="min-h-[36px] rounded-md border border-[#007a9e] px-3 text-sm font-semibold text-[#005f7b] hover:bg-[#eef6f9]" data-testid={`switch-preview-${row.id}`}>
        {row.scoring_version === "1.0" ? "Preview switch to v1.1" : "Preview revert to v1.0"}
      </button>
    </td>
  </tr>
);

const Preview = ({ pv, onApply, onClose, busy }) => {
  const [reason, setReason] = useState("");
  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="switch-preview-panel">
      <h2 className="text-lg font-semibold">{pv.school_name} · {pv.period_label}: v{pv.from_version} → v{pv.to_version}</h2>
      <p className="mt-1 text-sm text-slate-600">Only these answers are re-scored. Every prior value is archived and can be switched back.</p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm" data-testid="switch-changes-table">
          <thead><tr className="text-left text-slate-500"><th className="py-1 pr-3">Question</th><th className="py-1 pr-3">Saved answer</th><th className="py-1 pr-3">Points now</th><th className="py-1">Points after</th></tr></thead>
          <tbody>{pv.changes.map((c) => (
            <tr key={c.indicator_id} className="border-t border-slate-100" data-testid={`switch-change-${c.indicator_id}`}>
              <td className="py-1.5 pr-3">{c.indicator_name}</td>
              <td className="py-1.5 pr-3">{c.is_not_applicable ? "N/A" : fmt(c.raw_value)}</td>
              <td className="py-1.5 pr-3">{fmt(c.points_before)} / {c.max_points}</td>
              <td className="py-1.5 font-semibold">{c.points_after} / {c.max_points}</td>
            </tr>))}
            {!pv.changes.length && <tr><td colSpan={4} className="py-2 text-slate-500">None of the affected questions are answered yet — only the version changes.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-sm" data-testid="switch-overall">Overall score: <b>{fmt(pv.summary_before.overall)}</b> → <b>{fmt(pv.summary_after.overall)}</b></p>
      {pv.is_final ? <p className="mt-3 text-sm font-semibold text-red-700">This assessment is final and can't be switched in this release.</p> : (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
          <label className="flex-1 text-sm font-medium">Reason (recorded in the audit trail)
            <input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-normal" placeholder="e.g. Approved by Dirk and Peter for the founding partner pilot" data-testid="switch-reason-input" />
          </label>
          <button type="button" disabled={busy || !reason.trim()} onClick={() => onApply(reason)} className="min-h-[40px] rounded-md bg-[#007a9e] px-4 font-semibold text-white disabled:opacity-50" data-testid="switch-apply-button">Confirm switch</button>
          <button type="button" onClick={onClose} className="min-h-[40px] rounded-md px-4 font-semibold text-[#005f7b]" data-testid="switch-cancel-button">Cancel</button>
        </div>
      )}
    </section>
  );
};

export default function ScoringVersionSwitch() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => call("candidates").then((d) => setRows(d.items)).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const preview = (row) => call("preview", { diagnostic_id: row.id, target_version: target(row) }).then(setPv).catch((e) => toast.error(e.message));
  const apply = async (reason) => {
    setBusy(true);
    try {
      await call("apply", { diagnostic_id: pv.diagnostic_id, target_version: pv.to_version, reason });
      toast.success(`Switched to v${pv.to_version}`); setPv(null); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const shown = (rows || []).filter((r) => !q || `${r.school_name} ${r.period_label}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="max-w-5xl" data-testid="scoring-switch-page">
      <h1 className="text-2xl font-semibold">Scoring version switch</h1>
      <p className="mt-1 text-sm text-slate-600">In-progress diagnostics only. Each switch is audited and reversible.</p>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search school or period" className="mt-4 w-full max-w-sm rounded-md border border-slate-300 px-3 py-2" data-testid="switch-search-input" />
      {pv && <Preview pv={pv} onApply={apply} onClose={() => setPv(null)} busy={busy} />}
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white p-4">
        <table className="w-full min-w-[640px] text-sm" data-testid="switch-candidates-table">
          <thead><tr className="text-left text-slate-500"><th className="pb-2">School</th><th className="pb-2">Period</th><th className="pb-2">Status</th><th className="pb-2">Scoring</th><th /></tr></thead>
          <tbody>{shown.map((r) => <Row key={r.id} row={r} onPreview={preview} />)}</tbody>
        </table>
        {rows && !shown.length && <p className="py-3 text-sm text-slate-500">No in-progress diagnostics match.</p>}
      </div>
    </div>
  );
}
